import {useLocale} from '@aboutyou/i18n';
import React, {FC, useRef} from 'react';
import {useRect} from 'react-viewport-utils';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {mobileOnly, tabletAndUp} from '../../../constants/customMedia';
import {overflowEllipsis} from '../../../constants/ellipsis';
import {Z_INDEXES} from '../../../constants/layers';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {BackButton} from '../../atoms/BackButton/BackButton';
import {NumberBadge} from '../../atoms/NumberBadge/NumberBadge';
import {HEADER_HEIGHT} from '../../organisms/Header/Header';

type stickyType =
  | {stickyOnScrollUp: false; showBanner?: never}
  | {stickyOnScrollUp: true; showBanner: boolean};

interface CategoryHeaderProps {
  backTarget?: string;
  onBack?: VoidFunction;
  categoryName?: string;
  subtitle?: string;
  loading?: boolean;
  productCount?: number;
  centered: boolean;
  className?: string;
  sidePadding: boolean;
  offsetTop?: number;
}

type Props = CategoryHeaderProps & stickyType;

export const CategoryHeader: FC<React.PropsWithChildren<Props>> = ({
  backTarget,
  onBack,
  categoryName,
  subtitle,
  productCount,
  stickyOnScrollUp,
  centered,
  showBanner,
  className,
  sidePadding,
  children,
  offsetTop = 0,
}) => {
  const locale = useLocale();
  const showBackButton = backTarget || onBack;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {height: wrapperHeight} = useRect(wrapperRef, {
    disableScrollUpdates: true,
    priority: 'low',
  }) || {height: 0};

  return (
    <Wrapper
      ref={wrapperRef}
      data-testid="categoryHeader"
      $stickyOnScrollUp={stickyOnScrollUp}
      $sidePadding={sidePadding}
      $showBanner={showBanner ?? false}
      $centered={centered}
      $offsetTop={offsetTop}
      $wrapperHeight={wrapperHeight}
      className={className}
    >
      {showBackButton && (
        <StyledBackButton
          testId="categoryHeaderBackButton"
          href={backTarget}
          onClick={onBack}
          considerScrollTopAnchor
        />
      )}
      <TitleWrapper data-testid="categoryHeaderTitle">
        <Title $centered={Boolean(showBackButton && centered)}>
          {categoryName}
          {subtitle && <small> {subtitle}</small>}
        </Title>
        {productCount !== undefined && (
          <StyledNumberBadge
            count={productCount.toLocaleString(locale.replace('_', '-'))}
            size="small"
          />
        )}
      </TitleWrapper>
      {children}
    </Wrapper>
  );
};

const Wrapper = styled.header<{
  $stickyOnScrollUp: boolean;
  $centered: boolean;
  $showBanner: boolean;
  $sidePadding: boolean;
  $offsetTop: number;
  $wrapperHeight: number;
}>`
  position: sticky;
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  align-items: ${props => (props.$centered ? 'center' : 'start')};

  height: 4.375rem;
  padding: ${props => (props.$sidePadding ? '0 1rem' : 0)};

  top: ${HEADER_HEIGHT}px;
  z-index: ${Z_INDEXES.SUB_HEADER};
  background-color: ${COLORS.White};
  transition: transform 150ms linear;
  transform: ${({$showBanner, $offsetTop, $wrapperHeight}) =>
    $showBanner ? `translateY(${$wrapperHeight + $offsetTop}px)` : 'none'};

  ${({$stickyOnScrollUp, $wrapperHeight}) =>
    $stickyOnScrollUp &&
    css`
      top: -${$wrapperHeight}px;
      border-bottom: 1px solid ${COLORS.Grey3};
    `}
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 0;
  flex: 1 1 0;
  height: inherit;

  ${tabletAndUp(css`
    display: block;
    min-width: unset;
    flex: 0 0 auto;
  `)}
`;

const Title = styled.h1<{$centered: boolean}>`
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.bold};
  color: ${COLORS.Black};
  line-height: 1.5;

  ${({$centered}) =>
    $centered &&
    css`
      text-align: center;
      position: absolute;
      width: 100%;
      left: 0;
    `}

  ${mobileOnly(css`
    ${overflowEllipsis}

    small {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `)}

  ${tabletAndUp(css`
    display: inline-block;

    small {
      margin-left: 4px;
    }
  `)}

  small {
    font-size: ${FONT_SIZES.px14};
    font-weight: ${FONT_WEIGHTS.light};
    color: ${COLORS.Grey1};
  }
`;

const StyledBackButton = styled(BackButton)`
  &&& {
    margin-left: -17px;
    z-index: 1;
  }
`;

const StyledNumberBadge = styled(NumberBadge)`
  color: ${COLORS.Grey1};
  background-color: ${COLORS.Grey4};
  margin-left: 5px;
  vertical-align: 3px;
  height: 24px;
  padding-inline: 12px;
`;
